import { Injectable } from '@angular/core';
import { Feature, Map, Overlay, View } from 'ol';
import { fromLonLat, toLonLat } from 'ol/proj';
import { BehaviorSubject } from 'rxjs';
import PinchZoom from 'ol/interaction/PinchZoom';
import MouseWheelZoom from 'ol/interaction/MouseWheelZoom';
import DoubleClickZoom from 'ol/interaction/DoubleClickZoom';
import DragZoom from 'ol/interaction/DragZoom';
import DragPan from 'ol/interaction/DragPan';
import { GlobalVars } from '../global-vars';
import { SidebarService } from './sidebar.service';
import { LocalStorageService } from './local-storage.service';
import { Geometry, MultiPolygon, Polygon } from 'ol/geom';
import { environment } from '../../../environments/environment';
import { SharedDateService } from './shared-date.service';
import { GeoJSON } from 'ol/format';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import { SymbologyService } from './symbology.service';
import TileLayer from 'ol/layer/Tile';
import { OSM } from 'ol/source';
import { Control } from 'ol/control';
import { LayerService } from './layer.service';

@Injectable({
  providedIn: 'root'
})
export class MapService {
  public map: Map | undefined;
  public sectorLayer!: VectorLayer<any>;

  private zoomLevel = new BehaviorSubject<number>(0);
  zoomLevel$ = this.zoomLevel.asObservable();
  private zoomedRefresh = new BehaviorSubject<any>(undefined);
  zoomedRefresh$ = this.zoomedRefresh.asObservable();

  private defaultCenter = fromLonLat(GlobalVars.initialCoord);
  private defaultZoom = GlobalVars.initialZoom;

  private pinchZoomInteraction = new PinchZoom();
  private mouseWheelZoomInteraction = new MouseWheelZoom();
  private doubleClickZoomInteraction = new DoubleClickZoom();
  private shiftDragZoomInteraction = new DragZoom();
  private dragPanInteraction = new DragPan();


  constructor(
    private sidebarService: SidebarService,
    private localStorageService: LocalStorageService,
    private symbologyService: SymbologyService,
    private layerService: LayerService
  ){}

  // Initialise la carte
  createMap(
      target: string,
      controls: Control[],
      clickCallback: () => void): void {
    this.createSectorLayer(clickCallback);

    if (this.map) {
      this.map.setTarget(undefined);
    }
    this.map = new Map({
      target: target,
      layers: [
        new TileLayer({
          source: new OSM()
        }),
        this.sectorLayer
      ],
      view: new View({
        center: this.defaultCenter,
        zoom: this.defaultZoom,
      }),
      controls: controls
    });

    this.map.on('click', (evt) => {
      if (this.map) {
        this.map.forEachFeatureAtPixel(evt.pixel, (featureLike) => {
          const feature = featureLike as Feature<Geometry>;
          const geo = feature.getGeometry();
          if (geo && (geo.getType() === 'Polygon' || geo.getType() === 'MultiPolygon')) {
            this.handleSectorClick(geo);
          }
        });
        }
    });

    this.map.getView().on('change:center', this.toggleResetButton.bind(this));
    this.map.getView().on('change:resolution', this.toggleResetButton.bind(this));

    this.map.getView().on('change:resolution', () => {
      this.updateIconsBasedOnZoom(clickCallback);
    });

    this.restoreMapPosition();
  }

  // Centre la carte sur les coordonnées données (latitude, longitude)
  centerMap(lat: number, lon: number): void {
    if (this.map) {
      const view = this.map.getView();
      const coordinates = fromLonLat([lon, lat]);
      view.setCenter(coordinates);
      view.setZoom(9);
    }
  }

  getCurrentMapPosition() {
    if (this.map) {
      const center = toLonLat(this.map.getView().getCenter()!);
      const zoom = this.map.getView().getZoom();
      return { lat: center[1], lng: center[0], zoom: zoom! };
    }
    return null;
  }

  restoreMapPosition() {
    const savedPosition = localStorage.getItem('mapPreferences');
    if (savedPosition && this.map) {
      const { lat, lng, zoom } = JSON.parse(savedPosition);
      const view = this.map.getView();
      view.setCenter(fromLonLat([lng, lat]));
      view.setZoom(zoom);
    }
  }

  setMapPosition( coords: [number, number], zoom: number ) {
    if (this.map) {
      const view = this.map.getView();
      const coordinates = fromLonLat(coords);
      view.setCenter(coordinates);
      view.setZoom(zoom);
    } else {
      console.error("La carte n'est pas initialisée.");
    }
  }

  updateZoomLevel(zoom: number): void {
    this.zoomLevel.next(zoom);
  }

  updateZoomedRefresh(info: any): void {
    this.zoomedRefresh.next(info);
  }

  getSectorLayerName() {
    const currentZoom = (this.map) ? this.map.getView().getZoom() : GlobalVars.initialZoom;
    const layerName = (currentZoom && currentZoom <= GlobalVars.wfsSimplifiedMaxZoomAndSensors) ? GlobalVars.sectorLayers.simplified : GlobalVars.sectorLayers.detailed;
    return layerName;
  }

  getSectorLayerType() {
    const currentZoom = (this.map) ? this.map.getView().getZoom() : GlobalVars.initialZoom;
    const layerType = (currentZoom && currentZoom <= GlobalVars.wfsSimplifiedMaxZoomAndSensors) ? GlobalVars.sectorLayersType.simplified : GlobalVars.sectorLayersType.detailed;
    return layerType;
  }

  toggleResetButton(): void {
    const currentCenter = this.map?.getView().getCenter();
    const currentZoom = this.map?.getView().getZoom();
    const resetButton = document.getElementById('resetViewButton');
    if (resetButton && currentCenter) {
      if (currentZoom !== GlobalVars.initialZoom || currentCenter[0] !== fromLonLat(GlobalVars.initialCoord)[0] || currentCenter[1] !== fromLonLat(GlobalVars.initialCoord)[1]) {
        resetButton.style.display = 'flex';
      } else {
        resetButton.style.display = 'none';
      }
    }
  }

  // Enable Zoom
  enableZoom() {
    if (this.map) {
      this.map.addInteraction(this.pinchZoomInteraction);
      this.map.addInteraction(this.mouseWheelZoomInteraction);
      this.map.addInteraction(this.doubleClickZoomInteraction);
      this.map.addInteraction(this.shiftDragZoomInteraction);

      const view = this.map.getView();
      view.setMinZoom(8.5);
    }
  }

  // Disable Zoom
  disableZoom() {
    if (this.map) {
      const view = this.map.getView();
      view.setMinZoom(GlobalVars.initialZoom);
      this.map.removeInteraction(this.pinchZoomInteraction);
      this.map.removeInteraction(this.mouseWheelZoomInteraction);
      this.map.removeInteraction(this.doubleClickZoomInteraction);
      this.map.removeInteraction(this.shiftDragZoomInteraction);
    }
  }

  // Enable Pan
  enablePan() {
    if (this.map && !this.map.getInteractions().getArray().includes(this.dragPanInteraction)) {
      this.map.addInteraction(this.dragPanInteraction);
    }
  }

  disablePan() {
    if (this.map) {
      this.map.getInteractions().getArray().forEach((interaction) => {
        if (interaction instanceof DragPan && this.map) {
          this.map.removeInteraction(interaction);
        }
      });
    }
  }

  resetView(): void {
    this.sidebarService.setSidebarStatus(false);

    if (this.localStorageService.hasMapPreferences()) {
      this.restoreMapPosition();
    } else if(this.map) {
      this.map.getView().setZoom(GlobalVars.initialZoom);
      this.map.getView().setCenter(fromLonLat(GlobalVars.initialCoord));
    }

  }

  generateBoundingBoxHubeau(bottomLeft: [number, number], topRight: [number, number]): string {
    const [minX, minY] = toLonLat(bottomLeft);
    const [maxX, maxY] = toLonLat(topRight);
    return `${minX},${minY},${maxX},${maxY}`;
  }

  handleSectorClick(geometry: Geometry | undefined): void {
    if (geometry && this.map) {
      this.map.getView().fit(geometry.getExtent(), {
        duration: 0,
        maxZoom: 18, // Maximum zoom level when fitting
        padding: [10, 10, 10, 10] // Optional padding around the extent
      });

      const currentZoom = this.map.getView().getZoom();
      if (currentZoom && currentZoom <= GlobalVars.wfsSimplifiedMaxZoomAndSensors) { this.map.getView().setZoom(GlobalVars.wfsSimplifiedMaxZoomAndSensors + 0.1);
      }
    }
  }

  sortedWfsData: any[] = [];

  fetchWfsData(url: string, baseDate: Date): Promise<Feature[]> {
    return fetch(url)
      .then(response => {
        return response.json();
      })
      .then(data => {
        let features = data.features;
        if (!features || features.length === 0) {
          return [];
        }

        const today = new Date();
        const isBaseDateToday =
          baseDate.getFullYear() === today.getFullYear() &&
          baseDate.getMonth() === today.getMonth();

        if (isBaseDateToday) {
          // Cas 1 : date de départ = aujourd’hui → garder le plus récent
          features.sort((a: any, b: any) => {
            const aEnd = new Date(a.properties.end_period).getTime();
            const bEnd = new Date(b.properties.end_period).getTime();
            return bEnd - aEnd;
          });
          const latestEnd = features[0]?.properties?.end_period;

          features = features.filter(
            (f: { properties: { end_period: any } }) => f.properties.end_period === latestEnd
          );
        } else {
          // Cas 2 : date de départ ≠ aujourd’hui → garder uniquement le mensuel
          const firstDay = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);

          // Récupérer toutes les start_period uniques
          const uniqueStartPeriods: string[] = Array.from(
            new Set(features.map((f: { properties: { start_period: any; }; }) => f.properties.start_period))
          );

          // Trouver celle la plus proche du premier jour du mois
          let closestStart: string = uniqueStartPeriods[0];
          let minDiff = Math.abs(new Date(closestStart).getTime() - firstDay.getTime());

          uniqueStartPeriods.forEach((sp: string) => {
            const diff = Math.abs(new Date(sp).getTime() - firstDay.getTime());
            if (diff < minDiff) {
              closestStart = sp;
              minDiff = diff;
            }
          });

          // Filtrer les features avec start_period = closestStart
          features = features.filter((f: { properties: { start_period: string; }; }) => f.properties.start_period === closestStart);
        }

        // Récupérer l'ultimatumId du premier élément
        const ultimatumId = features[0]?.properties?.ultimatum_id;

        // Filtrer les features avec le même ultimatumId
        features = features.filter(
          (feature: { properties: { ultimatum_id: any } }) => feature.properties.ultimatum_id === ultimatumId
        );
        // Reduce features to the most recent update_date per sector_id
        const uniqueFeaturesMap = features.reduce((acc: any, feature: any) => {
          const sectorId = feature.properties.sector_id;
          if (
            !acc[sectorId] ||
            new Date(feature.properties.update_date).getTime() >
              new Date(acc[sectorId].properties.update_date).getTime()
          ) {
            acc[sectorId] = feature;
          }
          return acc;
        }, {});
        const uniqueFeatures = Object.values(uniqueFeaturesMap);

        data.features = uniqueFeatures;

        // Transformer en format OpenLayers
        const olFeatures = new GeoJSON().readFeatures(data, {
          featureProjection: 'EPSG:3857', // Adapter si besoin à ta projection
        });

        return olFeatures;
      })
      .catch(error => {
        console.error('❌ Error fetching WFS data:', error);
        return []; // Retourner un tableau vide en cas d'erreur
      });
  }



  createSectorWfsUrl(date: Date, zoomedRefresh: boolean = false) {
    let verifiedDate: Date;
    // Si la date est null, undefined, ou n'est pas un objet Date valide
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      verifiedDate = new Date();
    }else {
      verifiedDate = date;
    }
    const month = (verifiedDate.getMonth() + 1).toString().padStart(2, '0');
    const year = verifiedDate.getFullYear().toString();
    const day = verifiedDate.getDate().toString().padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;
    const layerName = this.getSectorLayerName();

    const communicate = true; // Récupération de tous les secteurs, même ceux non communiqués
    const isParentSector = true; // Recuperation du secteur parent, pas les sous-secteurs
    const isActiveSector = true; // true : Récupération des secteurs actifs, false : récupération des secteurs inactifs (null : non definis)

    let filter = `<Filter xmlns:gml="http://www.opengis.net/gml"><And>
      <PropertyIsLessThanOrEqualTo>
        <PropertyName>start_period</PropertyName>
        <Literal>${formattedDate}</Literal>
      </PropertyIsLessThanOrEqualTo>
      <PropertyIsGreaterThanOrEqualTo>
        <PropertyName>end_period</PropertyName>
        <Literal>${formattedDate}</Literal>
      </PropertyIsGreaterThanOrEqualTo>`;

    if (zoomedRefresh) {
      const bboxParams = this.getBboxParam();
      if (bboxParams) {
        filter += `<BBOX>
        <PropertyName>geom</PropertyName>
        <gml:Box srsName="EPSG:4326">
          <gml:coordinates>${bboxParams.xmin},${bboxParams.ymin} ${bboxParams.xmax},${bboxParams.ymax}</gml:coordinates>
        </gml:Box>
      </BBOX>`
      }
    }

    filter += `<PropertyIsEqualTo><PropertyName>communicate</PropertyName><Literal>${communicate}</Literal></PropertyIsEqualTo>`;
    filter += `<PropertyIsEqualTo><PropertyName>is_parent</PropertyName><Literal>${isParentSector}</Literal></PropertyIsEqualTo>`;
    filter += `<PropertyIsEqualTo><PropertyName>visualizer</PropertyName><Literal>${isActiveSector}</Literal></PropertyIsEqualTo>`;

    filter += `</And></Filter>`;

    const wfsUrl = `${environment.wfsBaseUrl}/indicateur_bsn/ows?service=WFS&version=1.0.0&request=GetFeature&outputFormat=application%2Fjson`
      + `&typeName=indicateur_bsn:${layerName}`
      + `&filter=${encodeURIComponent(filter)}`;

    return wfsUrl;
  }


  removeSectorIcons() {
    const overlaysToRemove: Overlay[] = [];
    if(this.map){
      const overlays = this.map.getOverlays().getArray();
      overlays.forEach((overlay) => {
        const element = overlay.getElement();
        if (element && element.classList.contains('sector-icon')) {
          overlaysToRemove.push(overlay);
        }
      });
      overlaysToRemove.forEach((overlay) => {
        if(this.map)
        this.map.removeOverlay(overlay);
      });
    }

  }

  getBboxParam(){
    if(this.map){
      const extent = this.map.getView().calculateExtent(this.map.getSize());
      const bottomLeft = [extent[0], extent[1]];
      const topRight = [extent[2], extent[3]];
      const [minX, minY] = toLonLat(bottomLeft);
      const [maxX, maxY] = toLonLat(topRight);
      const bboxParams = { ymin: minY, ymax: maxY, xmin: minX, xmax: maxX };
      return bboxParams;
    }
    return null;
  }

  /**
   * Create the layer to display the sectors
   */
  createSectorLayer(clickCallback: () => void) {
    const layerType = this.getSectorLayerType();
      const vectorSource = new VectorSource({
        format: new GeoJSON(),
        url: ''
      });
      this.sectorLayer = new VectorLayer({
        source: vectorSource,
        style: this.symbologyService.setSectorLayerStyle(layerType)
      });
      this.sectorLayer.getSource()?.on('addfeature', (event) => {
        const feature = event.feature;
        this.addSectorIcon(feature, clickCallback);
      });
      return this.sectorLayer;

  }

  addSectorIcons(clickCallback: () => void) {
    const features = this.sectorLayer.getSource()?.getFeatures();
    const layerType = this.getSectorLayerType();
    if(features && layerType === GlobalVars.sectorLayersType.simplified)
    features.forEach((feature) => {
      this.addSectorIcon(feature, clickCallback);
    });
  }

  addSectorIcon(feature: Feature<Geometry>, clickCallback: () => void): void {
    const geometry = feature.getGeometry();
    const layerType = this.getSectorLayerType();

    if ((geometry instanceof Polygon || geometry instanceof MultiPolygon) && layerType === GlobalVars.sectorLayersType.simplified) {
      let center;

      if (geometry instanceof Polygon) {
        center = geometry.getInteriorPoint().getCoordinates();
      } else if (geometry instanceof MultiPolygon) {
        const polygons = geometry.getPolygons();
        const largestPolygon = polygons.sort((a, b) => b.getArea() - a.getArea())[0];
        center = largestPolygon.getInteriorPoint().getCoordinates();
      }
      // Add icon at the center
      const iconElement = this.symbologyService.createIconElement(feature.get("tendency") ?? null);
      iconElement.addEventListener('click', () => {
        const geometry = feature.getGeometry();
        this.handleSectorClick(geometry);
        clickCallback();
      });

      const iconOverlay = new Overlay({
        position: center,
        positioning: 'center-center',
        element: iconElement
      });

      if (this.map)
        this.map.addOverlay(iconOverlay);
    }
  }

  updateIconsBasedOnZoom(clickCallback: () => void) {
    if (this.map) {
      const currentZoom = this.map.getView().getZoom();
      const layerState = this.layerService.getLayerState();
      if ((currentZoom && currentZoom > 8) || !layerState.bsn) {
        this.removeSectorIcons();
      } else {
        this.addSectorIcons(clickCallback);
      }
    }
  }


}
